/**
 * Theme Context Provider
 * Manages theme mode (light/dark) switching
 *
 * Colour CSS variables are DERIVED from tokens/colors.js via
 * applyCssColorVariables — not defined independently in CSS.
 */

import React, { createContext, useContext, useState, useMemo, useLayoutEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from './theme';
import { applyCssColorVariables } from './tokens/cssColorVariables';

const ThemeContext = createContext({
    mode: 'light',
    toggleTheme: () => { },
    setThemeMode: () => { },
});

const ThemeLoadingContext = createContext({
    pendingCount: 0,
    isLoading: false,
    context: null,
});

export const useThemeMode = () => useContext(ThemeContext);
export const useThemeLoading = () => useContext(ThemeLoadingContext);

const readInitialMode = () => {
    if (typeof window === 'undefined') return 'light';
    const saved = localStorage.getItem('themeMode');
    return saved === 'dark' || saved === 'light' ? saved : 'light';
};

export const ThemeProvider = ({ children, loadingState }) => {
    const [mode, setMode] = useState(readInitialMode);

    // Sync body class + derived CSS colour variables from tokens
    useLayoutEffect(() => {
        localStorage.setItem('themeMode', mode);
        applyCssColorVariables(mode);

        if (mode === 'dark') {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
    }, [mode]);

    const toggleTheme = () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
    };

    const setThemeMode = (newMode) => {
        if (newMode === 'light' || newMode === 'dark') {
            setMode(newMode);
        }
    };

    const theme = useMemo(() => createAppTheme(mode), [mode]);

    const contextValue = useMemo(
        () => ({
            mode,
            toggleTheme,
            setThemeMode,
        }),
        [mode]
    );

    const loadingContextValue = useMemo(
        () => ({
            pendingCount: loadingState?.pendingCount || 0,
            isLoading: Boolean(loadingState?.isLoading),
            context: loadingState?.context || null,
        }),
        [loadingState?.pendingCount, loadingState?.isLoading, loadingState?.context]
    );

    return (
        <ThemeContext.Provider value={contextValue}>
            <ThemeLoadingContext.Provider value={loadingContextValue}>
                <MuiThemeProvider theme={theme}>
                    <CssBaseline />
                    {children}
                </MuiThemeProvider>
            </ThemeLoadingContext.Provider>
        </ThemeContext.Provider>
    );
};

export default ThemeProvider;
